import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './OutboxMenu.module.css';

export interface OutboxMenuAction {
  id: string;
  label: string;
  onSelect: () => void;
  /** Set apart below the rest, in red: it throws something away. */
  destructive?: boolean;
}

interface OutboxMenuProps {
  /** Where the menu hangs from, in viewport coordinates; null to close it. */
  anchor: DOMRect | null;
  actions: readonly OutboxMenuAction[];
  onDismiss: () => void;
  /**
   * The row the menu acts on, drawn lifted above a blurred screen at the
   * anchor, the way iOS presents a context menu. Without it the menu drops
   * down from the anchor over the screen as it is.
   */
  lifted?: React.ReactNode;
  /** Which edge of the anchor the menu lines up with. */
  align?: 'start' | 'end';
  'data-testid'?: string;
}

/** Room kept between the menu and the edge of the screen it sits in. */
const EDGE = 12;
/** Space between the anchor and the menu. */
const GAP = 8;

interface Placement {
  liftedTop: number;
  menuTop: number;
  left: number;
  right: number;
  width: number;
}

/**
 * The outbox's menus: the long-press menu on a row, and the ⋯ menus on the
 * outbox and on the preview sheet. Destructive actions sit in a group of
 * their own below the rest.
 *
 * Laid out over whatever it is rendered in rather than the viewport: an Ionic
 * modal can be transformed, which moves the box `position: fixed` resolves
 * against. So the layer measures itself and places the menu relative to its
 * own box, which comes out right either way.
 */
const OutboxMenu: React.FC<OutboxMenuProps> = ({
  anchor,
  actions,
  onDismiss,
  lifted,
  align = 'start',
  'data-testid': testId,
}) => {
  const layerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  useLayoutEffect(() => {
    if (!anchor || !layerRef.current || !menuRef.current) {
      setPlacement(null);
      return;
    }
    const layer = layerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const anchorTop = anchor.top - layer.top;
    const anchorHeight = lifted ? anchor.height : 0;
    const below = lifted
      ? anchorTop + anchor.height
      : anchor.bottom - layer.top;
    // A row near the bottom lifts up far enough for its menu to fit under it.
    const overflow = Math.max(
      0,
      below + GAP + menuHeight - (layer.height - EDGE)
    );
    const liftedTop = Math.max(EDGE, anchorTop - overflow);
    setPlacement({
      liftedTop,
      menuTop: lifted
        ? liftedTop + anchorHeight + GAP
        : Math.max(EDGE, below + GAP - overflow),
      left: anchor.left - layer.left,
      right: layer.right - anchor.right,
      width: anchor.width,
    });
  }, [anchor, lifted, actions.length]);

  // Read through a ref so a caller's fresh `onDismiss` on each render neither
  // re-registers the Escape listener nor sends focus back to the first item.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Focus lands on the first item when the menu opens, and only then: a
  // re-render while it is open leaves focus wherever the sender moved it.
  useEffect(() => {
    if (!anchor) return;
    menuRef.current
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
      ?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anchor]);

  if (!anchor) return null;

  const regular = actions.filter(action => !action.destructive);
  const destructive = actions.filter(action => action.destructive);
  const renderAction = (action: OutboxMenuAction) => (
    <button
      key={action.id}
      type="button"
      role="menuitem"
      className={`${styles.item} ${action.destructive ? styles.destructive : ''}`}
      data-testid={`outbox-menu-${action.id}`}
      onClick={() => {
        onDismiss();
        action.onSelect();
      }}
    >
      {action.label}
    </button>
  );

  const horizontal =
    placement == null
      ? {}
      : align === 'end'
        ? { right: Math.max(EDGE, placement.right) }
        : { left: Math.max(EDGE, placement.left) };

  return (
    <div
      ref={layerRef}
      className={`${styles.layer} ${lifted ? styles.blurred : ''}`}
      data-testid={testId}
    >
      <div className={styles.backdrop} onClick={onDismiss} aria-hidden="true" />
      {lifted && placement && (
        <div
          className={styles.lifted}
          style={{
            top: placement.liftedTop,
            left: placement.left,
            width: placement.width,
          }}
          aria-hidden="true"
        >
          {lifted}
        </div>
      )}
      <div
        ref={menuRef}
        role="menu"
        className={styles.menu}
        style={{
          top: placement?.menuTop ?? 0,
          visibility: placement ? 'visible' : 'hidden',
          ...horizontal,
        }}
      >
        <div className={styles.group}>{regular.map(renderAction)}</div>
        {destructive.length > 0 && (
          <div className={styles.group}>{destructive.map(renderAction)}</div>
        )}
      </div>
    </div>
  );
};

export default OutboxMenu;
